/**
 * Layers cart drawer upsell (assets/layers-cart-recs.js, snippets/layers-cart-upsell.liquid).
 *
 * Same lifecycle as the DY upsell it replaces (assets/cart-upsell-carousel.js):
 *   - nothing loads until the drawer first opens (drawer--is-open, cart:open or ajaxProduct:added)
 *   - after ajaxProduct:added / cart:updated the block is requested again for the new cart,
 *     with in-cart products excluded and the first cart item as the anchor
 *   - the inline size select + add to cart posts to /cart/add.js and dispatches ajaxProduct:added,
 *     which the theme cart drawer listens to (rebuild + open)
 * An empty cart or an empty/failed block hides the upsell.
 */

const LT = await (window.LayersTheme?.ready ||
  new Promise((resolve) => document.addEventListener('layers:ready', (e) => resolve(e.detail), { once: true })));

const root = document.querySelector('[data-layers-cart-recs]');
const textNode = document.querySelector('[data-layers-cart-recs-text]');
let text = {};
try {
  text = JSON.parse((textNode && textNode.textContent) || '{}');
} catch {
  text = {};
}

const routeRoot = () => {
  const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return r.endsWith('/') ? r : `${r}/`;
};

let data;
let req = 0;
let lastKey = null;
let lastLoadAt = 0;

/* -------------------------------------------------------------- loading -- */

async function readCart() {
  const response = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) return null;
  return response.json();
}

function sizeIndex(product) {
  return (product.options || []).findIndex((o) => /^size$/i.test(o.name || ''));
}

function inlineSizes(product) {
  const idx = sizeIndex(product);
  const name = idx >= 0 ? product.options[idx].name : null;
  return (product.variants || []).map((v) => {
    const label = (name && (v.selectedOptions || []).find((o) => o.name === name)?.value) || v.title;
    const available = v.availableForSale !== false;
    return { id: v.numericId, label, available, text: available ? label : `${label} — ${text.soldOut || ''}` };
  });
}

async function load() {
  const layers = LT.sdk();
  if (!layers || !root) return;
  const token = ++req;
  let cart;
  try {
    cart = await readCart();
  } catch {
    cart = null;
  }
  if (token !== req) return;
  const ids = cart ? [...new Set(cart.items.map((item) => String(item.product_id)))] : [];
  if (!ids.length) {
    lastKey = null;
    data.items = [];
    root.hidden = true;
    return;
  }
  const key = ids.join(',');
  if (key === lastKey) return;
  lastKey = key;
  lastLoadAt = Date.now();

  if (!data.items.length) {
    data.loaded = false;
    root.hidden = false;
  }
  const sizes = LT.card.measuredImageSizes(root, '(min-width: 769px) 190px, 42vw');
  const controller = layers.blocks({
    blockId: root.dataset.blockId,
    anchorId: ids[0],
    excludeProductIds: ids,
    attributes: LT.card.attributes,
    pagination: { page: 1, limit: parseInt(root.dataset.limit, 10) || 10 },
    responseOptions: LT.card.mediaPreloads(sizes),
  });
  let result;
  try {
    result = await controller.get();
  } catch {
    result = { error: true };
  }
  controller.dispose();
  if (token !== req) return;
  const products = ((result && !result.error && result.data && result.data.products) || []).filter(LT.card.isMerchandise);
  if (!products.length) {
    lastKey = null;
    data.items = [];
    data.loaded = true;
    root.hidden = true;
    return;
  }
  data.items = products.map((p, i) => ({
    ...LT.card.toViewModel(p, { index: i, loading: i < 2 ? 'eager' : 'lazy', sizes, compact: true, quickShop: false }),
    inlineSizes: inlineSizes(p),
  }));
  data.loaded = true;
  root.hidden = false;
  await LT.alpine.Alpine.nextTick();
  LT.card.refresh(root);
  updateArrows();
}

let refreshTimer;
function scheduleRefresh() {
  if (!started) return;
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    // The first add opens the drawer and loads the row; skip an immediate second request.
    if (Date.now() - lastLoadAt < 1500) return;
    load();
  }, 800);
}

/* ------------------------------------------------------------ first open -- */

let started = false;
let classObserver = null;

function start() {
  if (started) return;
  started = true;
  if (classObserver) {
    classObserver.disconnect();
    classObserver = null;
  }
  document.removeEventListener('cart:open', start);
  document.removeEventListener('ajaxProduct:added', start);
  load();
}

function watchDrawer() {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;
  if (drawer.classList.contains('drawer--is-open')) {
    start();
    return;
  }
  classObserver = new MutationObserver(() => {
    if (drawer.classList.contains('drawer--is-open')) start();
  });
  classObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
}

/* ------------------------------------------------------- inline add to cart -- */

function setError(wrap, message) {
  if (!wrap) return;
  let el = wrap.querySelector('.grid-product__inline-error');
  if (!el && message) {
    el = document.createElement('div');
    el.className = 'grid-product__inline-error';
    wrap.appendChild(el);
  }
  if (el) el.textContent = message;
}

function onInlineAdd(event) {
  const btn = event.target.closest && event.target.closest('[data-inline-add]');
  if (!btn || btn.disabled || !root.contains(btn)) return;
  event.preventDefault();
  const wrap = btn.closest('[data-inline-atc]');
  const select = wrap && wrap.querySelector('[data-inline-size]');
  const variantId = select && select.value;
  if (!variantId) {
    setError(wrap, text.selectSize || '');
    return;
  }
  setError(wrap, '');
  btn.classList.add('is-loading');
  btn.disabled = true;
  fetch(`${routeRoot()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
  })
    .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
    .then(({ ok, json }) => {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      if (!ok || (json && json.status)) {
        setError(wrap, (json && (json.description || json.message)) || text.addError || '');
        return;
      }
      // The cart page has no drawer instance listening; reload like theme.js does there.
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: json } }));
    })
    .catch(() => {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      setError(wrap, text.addError || '');
    });
}

/* ---------------------------------------------------------------- arrows -- */

function scroller() {
  return root.querySelector('[data-layers-cart-scroller]');
}

function updateArrows() {
  const el = scroller();
  if (!el || !data) return;
  const max = el.scrollWidth - el.clientWidth;
  data.overflow = max > 4;
  data.atStart = el.scrollLeft <= 4;
  data.atEnd = el.scrollLeft >= max - 4;
}

function scrollByPage(direction) {
  const el = scroller();
  if (!el) return;
  const amount = Math.max(el.clientWidth * 0.8, 120);
  el.scrollBy({ left: direction * amount, behavior: 'smooth' });
}

/* ------------------------------------------------------------------ boot -- */

if (root) {
  LT.alpine.mount(root);
  data = LT.alpine.Alpine.$data(root);
  root.addEventListener('click', onInlineAdd);
  root.querySelector('[data-layers-cart-prev]')?.addEventListener('click', () => scrollByPage(-1));
  root.querySelector('[data-layers-cart-next]')?.addEventListener('click', () => scrollByPage(1));
  scroller()?.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', updateArrows, { passive: true });

  document.addEventListener('cart:open', start);
  document.addEventListener('ajaxProduct:added', start);
  document.addEventListener('ajaxProduct:added', scheduleRefresh);
  document.addEventListener('cart:updated', scheduleRefresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();
}
